import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, theme } from '../theme.js';
import { useAccess } from '../context/AuthContext.js';
import { useResolveIssue, useReopenIssue } from '../lib/queries.js';
import type { CheckItemDetail } from '../lib/queries.js';
import { actorLabel } from '../lib/people.js';
import { Icon } from './Icon.js';

/**
 * On a submitted check: record that an issue (missing / broken / attention /
 * flagged) has been fixed, or show who fixed it. The check's result stays.
 */
export function ResolveIssue({ orgId, item }: { orgId: string; item: CheckItemDetail }) {
  const { isAdmin } = useAccess();
  const resolveMut = useResolveIssue(orgId, item.id);
  const reopenMut = useReopenIssue(orgId, item.id);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const nav = useNavigate();
  const faultLink = item.faultId && (
    <button onClick={() => nav(`/faults/${item.faultId}`)} style={{ ...linkBtn, color: theme.accent, textDecoration: 'none', display: 'block' }}>
      Open in the fault register →
    </button>
  );

  const isIssue = ['missing', 'broken', 'attention'].includes(item.status ?? '') || item.flagged;
  if (!isIssue) return null;

  if (item.resolvedAt) {
    return (
      <div style={{ ...box, background: theme.status.ok.bg, borderColor: alpha(theme.status.ok.dot, 27) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.status.ok.fg, fontWeight: 700, fontSize: 14 }}>
          <Icon name="check" size={16} /> Fixed
        </div>
        <div style={{ fontSize: 13, color: theme.text, marginTop: 4 }}>{item.resolutionNote}</div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
          {actorLabel(item.resolvedByName, item.resolvedByActive)} ·{' '}
          {new Date(item.resolvedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
        </div>
        {isAdmin && (
          <button onClick={() => reopenMut.mutate()} disabled={reopenMut.isPending} style={linkBtn}>
            {reopenMut.isPending ? 'Reopening…' : 'Reopen (marked fixed by mistake)'}
          </button>
        )}
        {faultLink}
      </div>
    );
  }

  return (
    <div style={box}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={primary}>
          <Icon name="check" size={16} /> Mark as fixed
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) resolveMut.mutate(note.trim(), { onSuccess: () => setOpen(false) });
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>What was done?</div>
          <textarea
            autoFocus required rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Replaced with spare from store"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, borderRadius: 10,
              border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text,
              fontFamily: theme.font, resize: 'none',
            }}
          />
          {resolveMut.isError && (
            <div style={{ fontSize: 12, color: theme.status.danger.fg }}>{(resolveMut.error as Error).message}</div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setOpen(false)} style={{ ...primary, flex: 1, background: theme.surface, color: theme.textDim, border: `1px solid ${theme.border}` }}>
              Cancel
            </button>
            <button type="submit" disabled={!note.trim() || resolveMut.isPending} style={{ ...primary, flex: 2 }}>
              {resolveMut.isPending ? 'Saving…' : 'Mark fixed'}
            </button>
          </div>
        </form>
      )}
      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
        Closes it in the fault register. This check's result stays as recorded.
      </div>
      {faultLink}
    </div>
  );
}

const box: React.CSSProperties = {
  margin: '12px 16px 0', padding: 12, borderRadius: 12,
  background: theme.surface, border: `1px solid ${theme.border}`,
};

const primary: React.CSSProperties = {
  width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
  background: theme.status.ok.dot, color: '#fff', fontSize: 15, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};

const linkBtn: React.CSSProperties = {
  marginTop: 8, padding: 0, background: 'none', border: 'none', cursor: 'pointer',
  color: theme.textDim, fontSize: 12, fontWeight: 600, textDecoration: 'underline', fontFamily: theme.font,
};
